import React, { useState, useEffect } from 'react';
import Button from './Button';
import errorKbService from '../../services/errorKbService';
import './ErrorKbModal.css';

/**
 * Error Knowledge Base modal. Opened from the shared "KB?" trigger.
 *  - Entry exists                → shows the info (what the error means / how to fix it). Any user.
 *  - No entry yet, superadmin    → shows an "Add" button that reveals the create form.
 *  - Superadmin, entry exists     → an "Edit" button reveals the edit form.
 *
 * Props:
 *   isOpen, onClose
 *   entry          – existing KB entry ({ id, title, description, ... }) or null
 *   sampleMessage  – the raw error message the KB is about (shown for reference, saved as sample)
 *   errorSignature – normalised signature used as the match key
 *   syncTemplateId – template the knowledge is scoped to
 *   canEdit        – superadmin
 *   onSaved(entry) – called after a successful create/update so the parent can refresh
 */
export default function ErrorKbModal({ isOpen, onClose, entry, sampleMessage, errorSignature, syncTemplateId, canEdit, onSaved }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Start on the info / prompt screen; the user chooses Add or Edit to open the form.
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setTitle(entry?.title || '');
            setDescription(entry?.description || '');
            setError(null);
            setSaving(false);
            setEditing(false);
        }
    }, [isOpen, entry]);

    if (!isOpen) return null;

    const hasEntry = !!entry?.id;

    const handleSave = async () => {
        if (!title.trim() || !description.trim()) { setError('Title and description are required'); return; }
        setSaving(true); setError(null);
        try {
            if (entry?.id) {
                await errorKbService.update(entry.id, { title: title.trim(), description: description.trim() });
            } else {
                await errorKbService.create({
                    syncTemplateId, errorSignature, sampleMessage,
                    title: title.trim(), description: description.trim(),
                });
            }
            onSaved && onSaved();
            onClose();
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to save Knowledge Base entry');
        } finally {
            setSaving(false);
        }
    };

    let headerTitle;
    if (editing) headerTitle = hasEntry ? 'Edit Knowledge Base Entry' : 'Add Knowledge Base Entry';
    else headerTitle = hasEntry ? 'Error Help' : 'Knowledge Base';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-md ekb-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{headerTitle}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {sampleMessage && (
                        <div className="ekb-sample">
                            <span className="ekb-label">Message</span>
                            <pre className="ekb-sample-text">{sampleMessage}</pre>
                        </div>
                    )}

                    {editing ? (
                        <>
                            <label className="ekb-field">
                                <span className="ekb-label">Title <span className="ekb-req">*</span></span>
                                <input type="text" value={title} maxLength={255}
                                    placeholder="Short summary of the error"
                                    onChange={e => { setTitle(e.target.value); setError(null); }} />
                            </label>
                            <label className="ekb-field">
                                <span className="ekb-label">What it means &amp; how to fix it <span className="ekb-req">*</span></span>
                                <textarea rows={6} value={description}
                                    placeholder="Explain the cause and the steps to resolve it."
                                    onChange={e => { setDescription(e.target.value); setError(null); }} />
                            </label>
                            <p className="ekb-scope-hint">
                                This applies to every matching error in this sync template.
                            </p>
                        </>
                    ) : hasEntry ? (
                        <>
                            <h3 className="ekb-view-title">{entry.title}</h3>
                            <div className="ekb-view-desc">{entry.description}</div>
                        </>
                    ) : (
                        <p className="ekb-empty">
                            {canEdit
                                ? 'No Knowledge Base entry exists for this message yet. Click Add to create one — it will apply to every matching message in this sync template.'
                                : 'No Knowledge Base help has been added for this message yet.'}
                        </p>
                    )}

                    {error && <div className="ekb-error">{error}</div>}
                </div>
                <div className="modal-footer">
                    {editing ? (
                        <>
                            <Button variant="secondary" onClick={() => (hasEntry ? setEditing(false) : onClose())} disabled={saving}>Cancel</Button>
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : hasEntry ? 'Save Changes' : 'Save'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={onClose}>Close</Button>
                            {canEdit && (
                                <Button onClick={() => setEditing(true)}>{hasEntry ? 'Edit' : 'Add'}</Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
